<template>
  <div v-if="showModal" class="modal" @click="handleBackdropClick">
    <div class="popup-content">
      <!-- Header -->
      <div class="popup-header">
        <div class="header-title">
          <i class="fa-solid fa-desktop text-primary"></i>
          <h3>Device Details - <span class="text-highlight">{{ username || 'Unknown' }}</span></h3>
        </div>
        <button @click="closeModal" class="close-btn" title="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="popup-body">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading device details...</p>
        </div>
        <div v-else-if="error" class="error-state">
          <i class="fa-solid fa-circle-exclamation"></i>
          <p>{{ error }}</p>
        </div>
        <div v-else class="details-container">
          <!-- Devices Section -->
          <div class="section-block">
            <div class="section-header">
              <div class="flex-align-center">
                <i class="fa-solid fa-mobile-screen-button section-icon"></i>
                <h4 class="section-title">User Logged On Devices</h4>
              </div>
              <span class="count-badge">{{ devices.length }}</span>
            </div>
            <div class="table-responsive" v-if="devices.length > 0">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Android Version</th>
                    <th>Low RAM</th>
                    <th>Login Time</th>
                    <th>Modified Details</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="device in devices" :key="device.id">
                    <td>
                      <div class="font-medium">{{ device.device_name }}</div>
                      <div class="text-muted" style="font-size: 12px; margin-top: 2px;">({{ device.device_id }})</div>
                    </td>
                    <td>
                      <div class="flex-align-center">
                        <i class="fa-brands fa-android android-icon"></i>
                        <span>{{ device.android_version }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="badge" :class="device.is_low_ram_device === '1' ? 'badge-warning' : 'badge-success'">
                        {{ device.is_low_ram_device === '1' ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>{{ formatDate(device.created_at) }}</td>
                    <td class="mod-details">
                      <div v-if="device.updated_at || device.changed_by || device.remark" class="mod-info-list">
                        <div v-if="device.updated_at" class="mod-item" title="Modified At">
                          <span class="mod-label"><i class="fa-regular fa-clock"></i></span> <span>{{ formatDate(device.updated_at) }}</span>
                        </div>
                        <div v-if="device.changed_by" class="mod-item" title="Modified By">
                          <span class="mod-label"><i class="fa-regular fa-user"></i></span> <span>{{ device.changed_by_name }}</span>
                        </div>
                        <div v-if="device.remark" class="mod-item" title="Remark">
                          <span class="mod-label"><i class="fa-regular fa-comment-dots"></i></span> <span class="text-truncate" :title="device.remark">{{ device.remark }}</span>
                        </div>
                      </div>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span class="badge" :class="device.is_active === '1' ? 'badge-success' : 'badge-error'">
                        {{ device.is_active === '1' ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <i class="fa-solid fa-inbox empty-icon"></i>
              <p>No devices found.</p>
            </div>
          </div>

          <!-- Dispatches Section -->
          <div class="section-block">
            <div class="section-header">
              <div class="flex-align-center">
                <i class="fa-solid fa-truck-fast section-icon"></i>
                <h4 class="section-title">Dispatches</h4>
              </div>
              <span class="count-badge">{{ dispatches.length }}</span>
            </div>
            <div class="table-responsive" v-if="dispatches.length > 0">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Dispatch No</th>
                    <th>Created By</th>
                    <th>Created At</th>
                    <th>Gate Pass In</th>
                    <th>Gate Pass Out</th>
                    <th>DSR Confirmed</th>
                    <th>DSR Confirmed At</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dispatch in dispatches" :key="dispatch.id">
                    <td class="font-medium">{{ dispatch.dispatch_no }}</td>
                    <td>{{ dispatch.created_by_name || dispatch.created_by || 'N/A' }}</td>
                    <td>{{ formatDate(dispatch.created_at) }}</td>
                    <td>
                      <span class="badge" :class="dispatch.is_gate_pass_in === '1' ? 'badge-success' : 'badge-error'">
                        {{ dispatch.is_gate_pass_in === '1' ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="dispatch.is_gate_pass_out === '1' ? 'badge-success' : 'badge-error'">
                        {{ dispatch.is_gate_pass_out === '1' ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>
                       <span class="badge" :class="dispatch.sales_rep_confirmed === '1' ? 'badge-success' : 'badge-warning'">
                        {{ dispatch.sales_rep_confirmed === '1' ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>
                      <span v-if="dispatch.sales_rep_confirmed === '1'">{{ formatDate(dispatch.rep_confirmed_at) }}</span>
                      <span v-else class="text-muted font-medium">-</span>
                    </td>
                    <td>
                      <span class="badge" :class="dispatch.is_active === '1' ? 'badge-success' : 'badge-error'">
                        {{ dispatch.is_active === '1' ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
               <i class="fa-solid fa-inbox empty-icon"></i>
               <p>No dispatches found.</p>
            </div>
          </div>

          <!-- Invoices Section -->
          <div class="section-block">
             <div class="section-header">
              <div class="flex-align-center">
                <i class="fa-solid fa-file-invoice-dollar section-icon"></i>
                <h4 class="section-title">Invoices</h4>
              </div>
              <span class="count-badge">{{ invoices.length }}</span>
            </div>
            <div class="table-responsive" v-if="invoices.length > 0">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Dealer Name</th>
                    <th class="text-right">Gross Total (Rs.)</th>
                    <th class="text-right">VAT (Rs.)</th>
                    <th class="text-right">Net Total (Rs.)</th>
                    <th class="text-right">Paid Amount (Rs.)</th>
                    <th class="text-right">Due Amount (Rs.)</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in invoices" :key="invoice.id">
                    <td class="font-medium">
                      {{ invoice.invoices_no }}
                      <i v-if="invoice.vat_liable === '1'" class="fa-solid fa-circle-check vat-icon" title="TAX Invoice"></i>
                    </td>
                    <td>{{ invoice.dealer_name }}</td>
                    <td class="text-right text-muted">{{ formatCurrency(invoice.gross_total) }}</td>
                    <td class="text-right text-muted">{{ formatCurrency(invoice.vat) }}</td>
                    <td class="text-right font-medium">{{ formatCurrency(invoice.net_total) }}</td>
                    <td class="text-right text-success">{{ formatCurrency(invoice.paid_amount) }}</td>
                    <td class="text-right text-danger font-medium">{{ formatCurrency(invoice.due_amount) }}</td>
                    <td>{{ formatDate(invoice.invoice_created_at) }}</td>
                    <td>
                      <span class="badge" :class="invoice.is_cancel === '1' ? 'badge-error' : 'badge-success'">
                        {{ invoice.is_cancel === '1' ? 'Cancelled' : 'Active' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
               <i class="fa-solid fa-inbox empty-icon"></i>
               <p>No invoices found.</p>
            </div>
          </div>

          <!-- Unlock User Section -->
          <div class="action-section">
            <div v-if="!canUnlock" class="alert-box alert-warning">
              <div class="alert-icon">
                <i class="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div class="alert-content">
                <strong>Action Required:</strong> Please gate pass in the following dispatch(es) before unlocking the user:
                <div class="dispatch-ids">{{ unreturnedDispatchesIds }}</div>
              </div>
            </div>
            
            <div v-else class="unlock-panel">
              <div class="unlock-info">
                <i class="fa-solid fa-user-lock"></i>
                <div class="unlock-text">
                  <h5>Unlock User Device</h5>
                  <p>Provide a reason for unlocking the user's active device.</p>
                </div>
              </div>
              <div class="unlock-form">
                <textarea 
                  v-model="remark" 
                  class="enterprise-input" 
                  placeholder="Enter remark here..." 
                  rows="2"
                  maxlength="200"
                ></textarea>
                <button 
                  class="enterprise-btn primary-btn" 
                  @click="processUnlock" 
                  :disabled="isUnlocking || !remark.trim()"
                >
                  <i class="fa-solid" :class="isUnlocking ? 'fa-circle-notch fa-spin' : 'fa-unlock'"></i> 
                  <span>{{ isUnlocking ? 'Unlocking...' : 'Unlock User' }}</span>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { viewMobileAppUserDetails, unlockMobileAppUser } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: 'DeviceDetailsModal',
  props: {
    showModal: Boolean,
    userId: [String, Number],
    username: String,
    token: { type: String, required: true }
  },
  emits: ['close', 'refresh'],
  setup(props, { emit }) {
    const toast = useToast();
    const loading = ref(false);
    const error = ref('');
    const devices = ref([]);
    const dispatches = ref([]);
    const invoices = ref([]);

    const remark = ref('');
    const isUnlocking = ref(false);

    const unreturnedDispatches = computed(() => {
      return dispatches.value.filter(d => d.is_gate_pass_in !== '1');
    });

    const canUnlock = computed(() => {
      return unreturnedDispatches.value.length === 0;
    });

    const unreturnedDispatchesIds = computed(() => {
      return unreturnedDispatches.value.map(d => d.dispatch_no).join(', ');
    });

    const closeModal = () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      emit('close');
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && props.showModal) closeModal();
    };

    const handleBackdropClick = (event) => {
      if (event.target.classList.contains('modal')) closeModal();
    };

    const fetchDetails = async (id) => {
      if (!id) return;
      try {
        loading.value = true;
        error.value = '';
        const response = await viewMobileAppUserDetails(id, props.token);
        if (response.data?.status === 'success') {
          const data = response.data.data;
          devices.value = data.user_logged_on_devices || [];
          dispatches.value = data.dispatches || [];
          invoices.value = data.invoices || [];
        } else {
          error.value = 'Failed to load details.';
        }
      } catch (err) {
        console.error(err);
        error.value = 'An error occurred while loading details.';
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString();
    };
    
    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    watch([() => props.userId, () => props.showModal], ([id, show]) => {
      if (show && id) fetchDetails(id);
    });

    const processUnlock = async () => {
      if (!props.userId) return;

      const activeDevice = devices.value.find(d => d.is_active === '1');
      if (!activeDevice) {
        toast.error('No active device log found to unlock.');
        return;
      }

      const payload = {
        user_id: props.userId,
        log_id: activeDevice.id,
        remark: remark.value,
        profile_id: localStorage.getItem('profile_id') || ''
      };

      try {
        isUnlocking.value = true;
        const response = await unlockMobileAppUser(props.token, payload);
        if (response.data.status === 'success') {
          toast.success(response.data.message || 'User unlocked successfully.');
          emit('refresh');
          closeModal();
        } else {
          toast.error(response.data.message || 'Unlock failed.');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'An error occurred while unlocking the user.';
        toast.error(errorMessage);
      } finally {
        isUnlocking.value = false;
      }
    };

    onMounted(() => document.addEventListener('keydown', handleEscape));
    onUnmounted(() => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    });

    return { 
      loading, error, devices, dispatches, invoices, closeModal, handleBackdropClick, 
      formatDate, formatCurrency, remark, isUnlocking, canUnlock, 
      unreturnedDispatchesIds, processUnlock 
    };
  }
};
</script>

<style scoped>
/* Enterprise Color Palette & Variables */
* {
  box-sizing: border-box;
}

/* Modal Overlay */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(17, 24, 39, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 24px;
}

/* Modal Content */
.popup-content {
  background-color: #ffffff;
  border-radius: 12px;
  width: 95vw;
  max-width: 1400px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

/* Fixed Header */
.popup-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 28px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  z-index: 10;
}
.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-title .text-primary {
  color: #3b82f6;
  font-size: 20px;
}
.popup-header h3 {
  margin: 0;
  color: #111827;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.01em;
}
.text-highlight {
  color: #2563eb;
}

/* Close Button */
.close-btn {
  background: #f3f4f6;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #6b7280;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}
.close-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

/* Scrollable Body */
.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
  background-color: #f9fafb;
}

/* Custom Scrollbar */
.popup-body::-webkit-scrollbar,
.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.popup-body::-webkit-scrollbar-track,
.table-responsive::-webkit-scrollbar-track {
  background: transparent;
}
.popup-body::-webkit-scrollbar-thumb,
.table-responsive::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}
.popup-body::-webkit-scrollbar-thumb:hover,
.table-responsive::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}

/* State Messages */
.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  color: #6b7280;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #6b7280;
  background: #ffffff;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}
@keyframes spin { 100% { transform: rotate(360deg); } }
.error-state {
  color: #ef4444;
  border-color: #fca5a5;
  background: #fef2f2;
}
.error-state i {
  font-size: 28px;
  margin-bottom: 12px;
}
.empty-icon {
  font-size: 32px;
  color: #d1d5db;
  margin-bottom: 12px;
}

/* Sections */
.details-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-block {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #ffffff;
}
.count-badge {
  background-color: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
}
.section-icon {
  color: #6b7280;
  margin-right: 12px;
  font-size: 16px;
}
.section-title {
  margin: 0;
  font-size: 16px;
  color: #111827;
  font-weight: 600;
}

/* Tables */
.table-responsive {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 280px;
  width: 100%;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
}
.data-table th {
  background-color: #f9fafb;
  padding: 12px 20px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 5;
  box-shadow: 0 1px 0 #e5e7eb; /* Replace border-bottom for sticky elements */
}
.data-table td {
  padding: 16px 20px;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  white-space: nowrap;
}
.data-table tbody tr:last-child td {
  border-bottom: none;
}
.data-table tbody tr:hover {
  background-color: #f9fafb;
}

/* Table Utility Classes */
.font-medium { font-weight: 500; color: #111827; }
.text-muted { color: #6b7280; }
.text-right { text-align: right; }
.text-success { color: #059669; }
.text-danger { color: #dc2626; }
.flex-align-center { display: flex; align-items: center; gap: 8px; }
.android-icon { color: #10b981; font-size: 16px; }
.vat-icon { color: #059669; margin-left: 6px; font-size: 14px; }

/* Mod Details */
.mod-info-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
.mod-item {
  display: flex;
  gap: 8px;
  align-items: center;
}
.mod-label {
  color: #9ca3af;
  font-size: 14px;
  width: 16px;
  display: flex;
  justify-content: center;
}
.text-truncate {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}
.badge-success { background-color: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }
.badge-error { background-color: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
.badge-warning { background-color: #fef3c7; color: #92400e; border: 1px solid #fde68a; }

/* Actions Section */
.action-section {
  margin-top: 8px;
}

/* Alert Box */
.alert-box {
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid transparent;
}
.alert-warning {
  background-color: #fffbeb;
  border-color: #fef08a;
}
.alert-warning .alert-icon {
  color: #d97706;
  font-size: 20px;
}
.alert-content {
  color: #92400e;
  font-size: 14px;
}
.dispatch-ids {
  margin-top: 8px;
  font-family: monospace;
  background: #fef3c7;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #fde68a;
  display: inline-block;
}

/* Unlock Panel */
.unlock-panel {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
}
@media (min-width: 768px) {
  .unlock-panel {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
}
.unlock-info {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.unlock-info i {
  background: #eff6ff;
  color: #3b82f6;
  padding: 12px;
  border-radius: 10px;
  font-size: 20px;
}
.unlock-text h5 {
  margin: 0 0 4px 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}
.unlock-text p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.unlock-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
@media (min-width: 768px) {
  .unlock-form {
    max-width: 500px;
    align-items: flex-end;
  }
}

.enterprise-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  background-color: #f9fafb;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
}
.enterprise-input:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.enterprise-input::placeholder {
  color: #9ca3af;
}

.enterprise-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}
.primary-btn {
  background-color: #2563eb;
  color: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
.primary-btn:hover:not(:disabled) {
  background-color: #1d4ed8;
}
.primary-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}
.primary-btn:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}
</style>